import type { StreamEvent } from './streamReducer'

/**
 * Parses an SSE stream from Foundry's Responses API into typed
 * `StreamEvent`s the reducer can consume.
 *
 * The Responses API replaces the classic Assistants/Threads/Runs API.
 * Event names follow the OpenAI Responses event taxonomy:
 *
 *     event: response.created
 *     data: {"response": {...}}
 *
 *     event: response.output_text.delta
 *     data: {"item_id": "...", "delta": "Hello "}
 *
 *     event: response.completed
 *     data: {"response": {...}}
 *
 * Reference:
 *   https://learn.microsoft.com/rest/api/aifoundry/aiproject#responses
 *   https://platform.openai.com/docs/api-reference/responses-streaming
 *
 * Frames are separated by blank lines; a frame can have multiple `data:`
 * lines which the spec says to concatenate with newlines. We rely on
 * Foundry emitting one data line per frame (true in practice), but
 * defensively handle multi-line data too.
 *
 * The browser's `EventSource` API would parse this for us — but it
 * only does GET requests and Foundry's responses endpoint is POST. So
 * we parse the response body as `ReadableStream<Uint8Array>` ourselves.
 */
export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncIterableIterator<StreamEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames end with double-newline. Process every complete frame
      // and keep the remainder in the buffer.
      let frameEnd: number
      while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, frameEnd)
        buffer = buffer.slice(frameEnd + 2)
        const event = parseFrame(frame)
        if (event) yield event
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function parseFrame(frame: string): StreamEvent | null {
  let eventName: string | null = null
  const dataLines: string[] = []

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.trimEnd()
    if (!line || line.startsWith(':')) continue // empty or comment
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }

  if (!eventName || dataLines.length === 0) return null

  let data: unknown
  try {
    data = JSON.parse(dataLines.join('\n'))
  } catch {
    return null
  }

  return mapFoundryEvent(eventName, data)
}

// ---------------------------------------------------------------------------
// Foundry Responses API event-name → StreamEvent mapping
//
// The Responses API streams a different event taxonomy from the classic
// Threads/Runs API. We keep the reducer's StreamEvent variants stable
// (so the render layer doesn't change) and map the new event names to
// them here. Anything not listed returns null and the reducer never
// sees it.
//
// Mapping summary:
//   response.created                          -> thread.run.created (carries response.id; we use it as
//                                                 the "thread" id for chaining via previous_response_id)
//   response.output_item.added (message)      -> thread.message.created
//   response.output_text.delta                -> thread.message.delta
//   response.output_item.done (message)       -> thread.message.completed
//   response.output_item.added (mcp_call)     -> thread.run.step.tool_call_started
//   response.mcp_call_arguments.delta         -> thread.run.step.tool_call_args_delta
//   response.output_item.done (mcp_call)      -> thread.run.step.tool_call_completed
//   response.completed                        -> thread.run.completed
//   response.failed                           -> thread.run.failed
//   response.error                            -> thread.run.failed (terminal stream error)
//   response.mcp_consent_required             -> thread.run.requires_action (OAuth consent link)
//   response.output_item.added (oauth_consent_request) -> thread.run.requires_action
//   response.output_item.added (mcp_approval_request)  -> thread.run.tool_approval_requested
// ---------------------------------------------------------------------------

interface ResponseEnvelope {
  response?: {
    id?: string
    status?: string
    error?: { message?: string } | null
    output?: unknown
    required_action?: {
      type?: string
      submit_tool_outputs?: {
        tool_calls?: unknown
      }
    }
  }
}

interface OutputItemPayload {
  item?: {
    id?: string
    type?: string
    name?: string
    arguments?: string
    output?: string
    /** mcp_approval_request only. */
    server_label?: string
    /** oauth_consent_request: URL the user must hit to grant consent. */
    consent_link?: string
    url?: string
  }
}

interface TextDeltaPayload {
  item_id?: string
  delta?: string
}

interface McpArgsDeltaPayload {
  item_id?: string
  delta?: string
}

interface ResponseErrorPayload {
  error?: { message?: string }
  message?: string
}

interface ConsentRequiredPayload {
  consent_link?: string
  url?: string
}

function mapFoundryEvent(eventName: string, data: unknown): StreamEvent | null {
  if (!isObject(data)) return null

  switch (eventName) {
    case 'response.created': {
      const p = data as ResponseEnvelope
      const id = p.response?.id
      if (typeof id !== 'string') return null
      // Single dispatch carries the response id forward; we use it
      // both as the conversation-chain key (via thread.created) and
      // as the run status flip.
      return { kind: 'thread.run.created', runId: id }
    }

    case 'response.in_progress':
      // No-op — covered by output_item.added handling for the actual
      // message/tool affordances.
      return null

    case 'response.output_item.added': {
      const p = data as OutputItemPayload
      const item = p.item
      if (!item || typeof item.id !== 'string') return null
      if (item.type === 'message') {
        return { kind: 'thread.message.created', messageId: item.id }
      }
      // MCP tool calls surface as `mcp_call` items. Their `name` is the
      // tool name; arguments stream via separate delta events.
      if (item.type === 'mcp_call' || item.type === 'function_call') {
        const toolName = item.name ?? ''
        if (!toolName) return null
        return {
          kind: 'thread.run.step.tool_call_started',
          stepId: item.id,
          toolName,
        }
      }
      // Per-tool approval gate. Foundry's MCP integration emits this
      // when the agent definition has `require_approval: "always"` (or
      // includes this tool in the `always` list). The arguments are
      // already complete on the item; there are no follow-up deltas —
      // the run ends here until the SPA POSTs an mcp_approval_response.
      if (item.type === 'mcp_approval_request') {
        const toolName = item.name ?? ''
        if (!toolName) return null
        return {
          kind: 'thread.run.tool_approval_requested',
          approvalRequestId: item.id,
          toolName,
          argumentsJson: item.arguments ?? '',
          serverLabel: item.server_label ?? '',
        }
      }
      // OAuth Identity Passthrough first-call gate. Same shape as the
      // standalone `response.mcp_consent_required` event handled below;
      // some Foundry builds emit it as a `response.output_item.added`
      // envelope instead — accept both.
      if (item.type === 'oauth_consent_request') {
        const link = item.consent_link ?? item.url ?? extractConsentLink(item)
        if (typeof link !== 'string') return null
        return { kind: 'thread.run.requires_action', consentLink: link }
      }
      // `mcp_list_tools` and other informational items are intentionally
      // ignored — they're internal Foundry plumbing, not user-visible.
      return null
    }

    case 'response.output_text.delta': {
      const p = data as TextDeltaPayload
      if (typeof p.item_id !== 'string' || typeof p.delta !== 'string') return null
      return { kind: 'thread.message.delta', messageId: p.item_id, textDelta: p.delta }
    }

    case 'response.output_text.done': {
      const p = data as TextDeltaPayload
      if (typeof p.item_id !== 'string') return null
      return { kind: 'thread.message.completed', messageId: p.item_id }
    }

    case 'response.mcp_call_arguments.delta':
    case 'response.function_call_arguments.delta': {
      const p = data as McpArgsDeltaPayload
      if (typeof p.item_id !== 'string' || typeof p.delta !== 'string') return null
      return { kind: 'thread.run.step.tool_call_args_delta', stepId: p.item_id, argsDelta: p.delta }
    }

    case 'response.output_item.done': {
      const p = data as OutputItemPayload
      const item = p.item
      if (!item || typeof item.id !== 'string') return null
      if (item.type === 'mcp_call') {
        // MCP tool: Foundry executed it; `output` carries the result.
        return {
          kind: 'thread.run.step.tool_call_completed',
          stepId: item.id,
          resultJson: item.output ?? '',
        }
      }
      if (item.type === 'function_call') {
        // Function tool: args streaming finished, but the call hasn't
        // executed yet — the SPA dispatches it once response.completed
        // arrives with `status: requires_action`. Stay quiet here so
        // the toolCall row remains `in_progress` until the reducer
        // transitions it via `thread.run.requires_tool_outputs`.
        return null
      }
      if (item.type === 'message') {
        return { kind: 'thread.message.completed', messageId: item.id }
      }
      return null
    }

    case 'response.completed': {
      const p = data as ResponseEnvelope
      const runId = p.response?.id
      // Function-tool flow: Foundry's `/openai/v1/responses` does NOT
      // pause runs with `status: "requires_action"` the way the older
      // Assistants API did. It emits `response.completed` with
      // `status: "completed"` and surfaces the function calls as
      // `function_call` items in `response.output`. The contract is
      // "if the output array contains function_call items, the caller
      // must execute them and POST `function_call_output` items back
      // with `previous_response_id`." So: check the output array on
      // every completion, regardless of `status`.
      if (typeof runId === 'string') {
        const pending = collectPendingFunctionCalls(p.response)
        if (pending.length > 0) {
          return { kind: 'thread.run.requires_tool_outputs', runId, toolCalls: pending }
        }
      }
      return { kind: 'thread.run.completed' }
    }

    case 'response.failed': {
      const p = data as ResponseEnvelope
      const msg = p.response?.error?.message ?? 'Response failed'
      return { kind: 'thread.run.failed', message: msg }
    }

    case 'response.error': {
      const p = data as ResponseErrorPayload
      const msg = p.error?.message ?? p.message ?? 'Stream error'
      return { kind: 'stream.error', message: msg }
    }

    // OAuth Identity Passthrough consent surfacing. Foundry's exact event
    // name for this in the Responses API varies — we accept several
    // documented variants plus `response.oauth_consent_requested`, which
    // is what Sweden Central actually emits as a top-level event
    // alongside the `output_item.added (oauth_consent_request)` item.
    // Either one carries the `consent_link`; handling them all defends
    // against Foundry dropping any single variant. The reducer flips
    // status to `consent_required` and the UI renders a clickable card.
    case 'response.mcp_consent_required':
    case 'response.requires_action':
    case 'response.mcp_consent_request':
    case 'response.oauth_consent_requested': {
      const p = data as ConsentRequiredPayload
      const link = p.consent_link ?? p.url ?? extractConsentLink(data)
      if (typeof link !== 'string') return null
      return { kind: 'thread.run.requires_action', consentLink: link }
    }

    default:
      return null
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

interface PendingFunctionCallShape {
  stepId: string
  callId: string
  name: string
  argumentsJson: string
}

/**
 * Walks the response payload for `function_call` items that need
 * SPA-side dispatch. Two sources:
 *   1. response.output[] — Foundry's `/openai/v1/responses` puts the
 *      pending calls here (status stays "completed", not
 *      "requires_action"). This is the production path.
 *   2. response.required_action.submit_tool_outputs.tool_calls — the
 *      older Assistants-API-style envelope. Some Azure/OpenAI builds
 *      surface it; we accept it defensively. De-dup by call id.
 */
function collectPendingFunctionCalls(
  response: ResponseEnvelope['response'],
): PendingFunctionCallShape[] {
  if (!response) return []
  const out: PendingFunctionCallShape[] = []
  const seen = new Set<string>()
  const tryAdd = (stepId: unknown, callId: unknown, name: unknown, args: unknown): void => {
    if (typeof stepId !== 'string' || stepId.length === 0) return
    if (typeof callId !== 'string' || callId.length === 0) return
    if (seen.has(callId)) return
    if (typeof name !== 'string' || name.length === 0) return
    seen.add(callId)
    out.push({
      stepId,
      callId,
      name,
      argumentsJson: typeof args === 'string' ? args : '',
    })
  }

  if (Array.isArray(response.output)) {
    for (const item of response.output) {
      if (!isObject(item)) continue
      if (item.type !== 'function_call') continue
      // The output-item id (`fc_...`) is what every SSE event uses to
      // address the toolCall row; the call_id (`call_...`) is the
      // handle Foundry wants on the echo. Carry both.
      const stepId = typeof item.id === 'string' ? item.id : ''
      const callId = typeof item.call_id === 'string' ? item.call_id : stepId
      tryAdd(stepId, callId, item.name, item.arguments)
    }
  }

  const ra = response.required_action
  if (ra && ra.type === 'submit_tool_outputs') {
    const calls = ra.submit_tool_outputs?.tool_calls
    if (Array.isArray(calls)) {
      for (const c of calls) {
        if (!isObject(c)) continue
        // Two known shapes — top-level fields, or a nested `function`
        // object. Accept both. On this path stepId and callId can be
        // the same (older envelope doesn't separate them).
        const fn = isObject(c.function) ? c.function : null
        const name = fn ? fn.name : c.name
        const args = fn ? fn.arguments : c.arguments
        tryAdd(c.id, c.id, name, args)
      }
    }
  }

  return out
}

function extractConsentLink(data: unknown): string | null {
  // Defensive fallback: walk the payload (objects AND arrays) for a
  // `consent_link` field anywhere inside.
  if (Array.isArray(data)) {
    for (const item of data) {
      const nested = extractConsentLink(item)
      if (nested !== null) return nested
    }
    return null
  }
  if (!isObject(data)) return null
  if (typeof data.consent_link === 'string') return data.consent_link
  for (const value of Object.values(data)) {
    const nested = extractConsentLink(value)
    if (nested !== null) return nested
  }
  return null
}
